import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';

import { ElementsState, runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementType,
  CanvasNamespaceId,
  ElementTargetPath,
  getRootElementId,
  LayoutData,
  NS_HOST_TYPE_PREFIX,
  PartialPropsOf,
  SlotIdOf,
} from '@blaze-runtime/engine-domain';
import {
  AICreatePageRequest,
  CreatePageRequest,
  NavigationLayout,
  PageInfo,
  PageType,
  ProjectInfo,
} from '@blaze-shared/api-contracts-domain';
import { DataBindingSourceInfo, PregenerationType } from '@blaze-shared/elements-domain';
import { PagesApiService } from '@blaze-shared/projects-data-access';
import {
  createUniqueSlug,
  isCustomerPortalProject,
  isNavigationPageType,
  NavigationPageType,
  PageCreationData,
} from '@blaze-shared/projects-domain';
import { toTitleCase } from '@blaze-shared/shared-util';
import { StylesApiService } from '@blaze-shared/theming-data-access';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { Connection, Workflow } from '@blaze-shared/workflow-domain';

import { isGeneratedForm } from '../canvas-elements/element-model/element-type-guards';
import { LayoutsApiDataRequest, LayoutsApiService } from '../data/layouts-api.service';
import { DialogElement } from '../elements/dialog/dialog.element';
import { DrawerElement } from '../elements/drawer/drawer.element';
import { RootElement } from '../elements/root/root.element';
import { isRoot } from '../utils/canvas-element/path';
import { elementsToEntityState, entityStateToElements } from '../utils/elements/element.utils';
import { populateNamespaceForHostTypeMutably } from '../utils/layout/update-namespace';

import { ElementDefinitionsService } from './element-definitions.service';
import {
  FormGenerationData,
  FormGenerationService,
  FormGenerationSourceService,
} from './forms/editor/public-api';
import { LayoutGenerationService } from './layout-generation.service';
import { ElementStylesService, getStyleReferences } from './styles';
import { FormSubmitWorkflowService } from './workflow-generation/public-api';

export type PageContainerElement = DialogElement | RootElement;

@Injectable({ providedIn: 'root' })
export class PageGenerationService {
  private readonly elementDefinitions = inject(ElementDefinitionsService);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly formGenerationService = inject(FormGenerationService);
  private readonly formGenerationSourceService = inject(FormGenerationSourceService);
  private readonly layoutGenerationService = inject(LayoutGenerationService);
  private readonly layoutsApiService = inject(LayoutsApiService);
  private readonly pagesApiService = inject(PagesApiService);
  private readonly store = inject(Store<ElementsState>);
  private readonly stylesApiService = inject(StylesApiService);
  private readonly workflowGenerationUtilsService = inject(FormSubmitWorkflowService);

  createPage(request: CreatePageRequest, pages: PageInfo[]): Observable<PageInfo> {
    // BL-6823: It's possible that creating a page with special characters will result in our inability to create
    // a page slug. When such a case is detected (empty slug), we force-create a random uuid slug.
    const urlSlug = createUniqueSlug(request.name, pages);
    return this.pagesApiService.createPage({
      ...request,
      urlSlug,
    });
  }

  createPageWithAI(request: AICreatePageRequest, pages: PageInfo[]): Observable<PageInfo> {
    const urlSlug = createUniqueSlug(request.name, pages);
    return this.pagesApiService.createPageWithAI({
      ...request,
      urlSlug,
    });
  }

  createHomePage(projectId: string, pages: PageInfo[]): Observable<PageInfo> {
    return this.createPage(
      {
        isHomePage: true,
        name: 'Home',
        pageType: 'Page',
        projectId,
      },
      pages,
    );
  }

  createNavigationPage(
    projectId: string,
    pageType: NavigationPageType,
    pages: PageInfo[],
    isUsedToCreateNewPages?: boolean,
  ): Observable<PageInfo> {
    return this.createPage(
      {
        isHomePage: false,
        name: pageType,
        pageType,
        projectId,
        isUsedToCreateNewPages,
      },
      pages,
    );
  }

  /**
   * @param namespaceId id of the page where `DialogElement` is placed
   * @returns array of elements with the layout (ready to be placed inside of the layout save object as `content`)
   */
  generateDefaultDialogLayout(
    namespaceId: CanvasNamespaceId,
    styleSheets: StyleSheet[],
    accountsTableName?: string,
    columnsCount?: number,
    initProps?: PartialPropsOf<'dialog'>,
  ): CanvasElement[] {
    const dialogDef = this.elementDefinitions.getDefinitionSafe('dialog');

    const dialogElement = {
      id: getRootElementId(namespaceId),
      ns: namespaceId,
      name: 'Popup Form',
      type: 'dialog',
      version: dialogDef.version,
      width: dialogDef.width,
      height: { ...dialogDef.height, value: 125 },
      slots: dialogDef.slots,
      props: { ...initProps },
    } as DialogElement;

    const formTargetPath = getFormTargetPath(dialogElement);
    const entityState = elementsToEntityState([dialogElement]);

    this.formGenerationService.regenerateFormMutably(entityState, styleSheets, {
      pregenerationType: 'custom',
      heightSettings: { mode: 'value', value: 125, fixed: false },
      formTargetPath,
      parentDialogId: dialogElement.id,
      accountsTableName,
      columnsCount,
    });

    return entityStateToElements(entityState);
  }

  /**
   * @param namespaceId id of the page where `DialogElement` is placed
   * @returns array of elements with the layout (ready to be placed inside of the layout save object as `content`)
   */
  generateDefaultDrawerLayout(
    namespaceId: CanvasNamespaceId,
    styleSheets: StyleSheet[],
    accountsTableName?: string,
    columnsCount?: number,
    initProps?: PartialPropsOf<'drawer'>,
  ): CanvasElement[] {
    const drawerDef = this.elementDefinitions.getDefinitionSafe('drawer');

    const drawerElement = {
      id: getRootElementId(namespaceId),
      ns: namespaceId,
      name: 'Drawer',
      type: 'drawer',
      version: drawerDef.version,
      width: drawerDef.width,
      height: drawerDef.height,
      slots: drawerDef.slots,
      props: { ...initProps },
    } as DrawerElement;

    const formTargetPath = getFormTargetPath(drawerElement);
    const entityState = elementsToEntityState([drawerElement]);

    this.formGenerationService.regenerateFormMutably(entityState, styleSheets, {
      pregenerationType: 'custom',
      heightSettings: { mode: 'value', value: 125, fixed: false },
      formTargetPath,
      parentDialogId: drawerElement.id,
      accountsTableName,
      columnsCount,
    });

    return entityStateToElements(entityState);
  }

  /**
   * @param pageId id of the page where `DialogElement` is placed
   * @param projectId
   * @param pregenerationType what type of dialog is generated
   * @param pageType
   * @param elementDataSource source of data for bindings creation
   * @param formGenerationData form generation data overrides
   * @param layout provided if page was already generated and should be updated
   * @returns Observable emitting the generated layout content, generated connections and workflow
   */
  regeneratePrefilledPageLayoutMutably(
    pageId: string,
    projectId: string,
    pregenerationType: PregenerationType,
    pageType: PageType,
    elementDataSource: DataBindingSourceInfo,
    formGenerationData?: Partial<FormGenerationData>,
    layout?: { elements: CanvasElement[]; connections: Connection[] },
  ): Observable<{
    layoutContent: CanvasElement[];
    connections: Connection[] | undefined;
    workflow: Workflow | undefined;
  }> {
    const namespaceId = pageId;

    // By default create a dialog
    let rootContainerType: CanvasElementType = 'dialog';
    let needsSubmitWorkflow = true;

    if (pageType === 'Page') {
      rootContainerType = 'root';
    } else if (pageType === 'Drawer') {
      rootContainerType = 'drawer';
    }

    // For 'view' create a page
    if (pregenerationType === 'view') {
      needsSubmitWorkflow = false;
    }

    // Prepare the root element
    let rootContainerElement: CanvasElement | undefined = layout?.elements?.find((element) =>
      isRoot(element),
    ); // Try to find existing root if original layout provided
    let entityState = elementsToEntityState(layout?.elements || []);

    // If root element missing
    if (!rootContainerElement) {
      // Generate new layout from scratch
      const rootContainerDef = this.elementDefinitions.getDefinitionSafe(rootContainerType);
      rootContainerElement = {
        id: getRootElementId(namespaceId),
        ns: namespaceId,
        name: toTitleCase(rootContainerType),
        type: rootContainerType,
        width: rootContainerDef.width,
        height:
          rootContainerType === 'dialog' || rootContainerType === 'drawer'
            ? { ...rootContainerDef.height, fixed: false, value: 125 }
            : rootContainerDef.height,
        slots: rootContainerDef.slots,
      } as PageContainerElement;
      entityState = elementsToEntityState([rootContainerElement]);
    }

    const formTargetPath = getFormTargetPath(rootContainerElement);
    const isDialog =
      rootContainerElement.type === 'dialog' || rootContainerElement.type === 'drawer';

    // Try to find existing generated form element
    const formElement = entityStateToElements(entityState).find(isGeneratedForm);

    return this.formGenerationSourceService
      .prepareFormGenerationDataAndPreloadSchemas(
        formElement,
        {
          formId: formElement?.id,
          formTargetPath,
          formTitle: '',
          pregenerationType,
          heightSettings: { mode: 'auto', fixed: false },
          parentDialogId: isDialog ? rootContainerElement.id : undefined,
          ...formGenerationData,
        },
        elementDataSource,
      )
      .pipe(
        concatLatestFrom(() => this.elementStylesService.sheets$),
        concatMap(([generationData, styleSheets]) => {
          const { formId, commitButtonId, formActionType } =
            this.formGenerationService.regenerateFormMutably(
              entityState,
              styleSheets,
              generationData,
            );

          if (!generationData.inputDataSource) {
            throw new Error('Input data source missing');
          }

          return this.workflowGenerationUtilsService.createOrUpdateSubmitWorkflowAndConnections(
            needsSubmitWorkflow ? projectId : undefined,
            namespaceId,
            formId,
            commitButtonId,
            formActionType,
            generationData.inputDataSource,
            { isDialog, layoutConnections: layout?.connections },
          );
        }),
        map((result) => ({
          layoutContent: entityStateToElements(entityState),
          connections: result ? [result.formConnection, result.buttonConnection] : [],
          workflow: result?.workflow,
        })),
        tap(({ workflow, connections }) => {
          // Add workflow and connections to store instantly to avoid asynchronous collisions
          this.store.dispatch(
            runtimeActions.prefilledPageLayoutGenerated({ workflow, connections }),
          );
        }),
      );
  }

  /**
   * Generates navigation pages with their layout for a new project based on the project's navigation layout.
   * Skips pages that already exist.
   * @param project
   * @param allPages
   * @returns Navigation pages generated for the project.
   */
  generateNavigationPagesForNewProject(
    project: ProjectInfo,
    allPages: PageInfo[],
  ): Observable<PageInfo[]> {
    const { aiDescription, projectSettingsInfo: { navigationLayout } = {} } = project;
    const wasProjectCreatedWithAI = !!aiDescription;
    const addAccountsDropdown = isCustomerPortalProject(project) && !project.isAdmin;
    const pageTypes = determinePageTypesForNavigationLayout(navigationLayout);

    const pagesToCreate$ = pageTypes
      .filter((pageType) => !allPages.some((page) => page.pageType === pageType))
      .map((pageType) => this.createNavigationPage(project.id, pageType, allPages, true));

    if (!pagesToCreate$.length) {
      return of([]);
    }

    // Navigation pages are found, create them and then update the layout
    return this.stylesApiService.getAllStyleSheets(project.id).pipe(
      map((styleSheets) => styleSheets.filter((sheet) => sheet.themeId === project.themeId)),
      switchMap((styleSheets) => {
        return forkJoin(pagesToCreate$).pipe(
          map((pages) => {
            const layoutUpdateRequests = pages
              .map((page) =>
                this.createLayoutUpdateRequestForNewPage(page, styleSheets, {
                  addAccountsDropdown,
                  navigationLayout,
                  menuPages: wasProjectCreatedWithAI ? allPages : undefined,
                }),
              )
              .filter((request) => !!request);

            return {
              pages,
              layoutUpdateRequests,
            };
          }),
          switchMap(({ pages, layoutUpdateRequests }) => {
            if (layoutUpdateRequests.length === 0) {
              return of(pages);
            }

            return this.layoutsApiService.updateLayouts(layoutUpdateRequests).pipe(
              map(() => pages),
              catchError((error) => {
                console.error('Error updating page layout:', error);
                return of(pages);
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        console.error('Error generating navigation pages for new project:', error);
        return of([]);
      }),
    );
  }

  /**
   * Adds default and navigation elements based on pages switch `isUsedToCreateNewPages`.
   * @param layout
   * @param allPages
   */
  initializeNewLayoutElements(layout: LayoutData, allPages: PageInfo[]): CanvasElement[] {
    const page = allPages.find((p) => p.id === layout.pageId);

    // Page must always be found
    if (!page) {
      return [];
    }

    const pageTypesToAdd = allPages
      .filter((page) => isNavigationPageType(page.pageType) && page.isUsedToCreateNewPages)
      .map((page) => page.pageType as NavigationPageType);

    const resultElements = this.layoutGenerationService.generateLayoutWithCreationData(page.id, {
      navigationElements: pageTypesToAdd,
    });

    // No elements were generated, no update needed
    if (!resultElements.length) {
      return [];
    }

    // Update host elements namespace
    populateNamespacesForHostElementsMutably(resultElements, allPages);

    return resultElements;
  }

  createPageWithLayout(
    projectId: string,
    pageType: PageType,
    navigationLayout: NavigationLayout | null,
    styleSheets?: StyleSheet[],
    pages: PageInfo[] = [],
  ): Observable<PageInfo> {
    if (!isNavigationPageType(pageType)) {
      return this.createPage(
        {
          projectId,
          name: pageType,
          pageType: pageType,
          isHomePage: false,
        },
        pages,
      );
    }

    return this.createNavigationPage(projectId, pageType, pages, true).pipe(
      switchMap((page) => {
        const request = this.createLayoutUpdateRequestForNewPage(page, styleSheets, {
          navigationLayout,
        });

        if (!request) {
          return of(page);
        }

        return this.layoutsApiService.updateLayouts([request]).pipe(
          map(() => page),
          catchError((error) => {
            console.error('Error updating navigation page layout', error);
            return of(page);
          }),
        );
      }),
    );
  }

  private createLayoutUpdateRequestForNewPage(
    page: PageInfo,
    styleSheets?: StyleSheet[],
    creationData?: PageCreationData,
  ): LayoutsApiDataRequest | undefined {
    let elements: CanvasElement[] = [];

    if (isNavigationPageType(page.pageType)) {
      elements = this.layoutGenerationService.generateDefaultNavigationPageLayout(
        page.id,
        page.pageType,
        creationData,
        styleSheets,
      );
    }

    if (creationData?.navigationElements) {
      elements = this.layoutGenerationService.generateLayoutWithCreationData(page.id, creationData);
    }

    if (!elements.length) {
      return undefined;
    }

    return {
      pageId: page.id,
      version: 0,
      uiLayer: { content: elements },
      actionLayer: { connections: [] },
      styles: getStyleReferences(elements, styleSheets ?? []),
    };
  }
}

function getFormTargetPath(rootContainerElement: CanvasElement): ElementTargetPath {
  if (
    rootContainerElement.type !== 'dialog' &&
    rootContainerElement.type !== 'drawer' &&
    rootContainerElement.type !== 'root'
  ) {
    throw new Error(`Unexpected element type: ${rootContainerElement.type}`);
  }

  return {
    id: rootContainerElement.id,
    parentId: rootContainerElement.id,
    targetPosition: { type: 'top' },
    index: 0, // Place as first in slot
    slot: resolveSlot(rootContainerElement.type, 'content', 'main'),
  };
}

/**
 * Returns the target slot based on the provided element type
 * @param elementType the element type to consider
 * @param dialog value returned if element is a dialog element
 * @param root value returned if element is a root element
 * @returns one of the values provided or undefined if type doesn't match any of the expected types
 */
function resolveSlot(
  elementType: CanvasElementType,
  dialogSlot: SlotIdOf<'dialog'>,
  rootSlot: SlotIdOf<'root'>,
): SlotIdOf<'dialog' | 'root'> {
  switch (elementType) {
    case 'dialog':
      return dialogSlot;
    case 'drawer':
      return dialogSlot;
    case 'root':
      return rootSlot;
    default:
      throw new Error('Unexpected element type');
  }
}

function populateNamespacesForHostElementsMutably(
  elements: CanvasElement[],
  pages: PageInfo[],
): void {
  for (const page of pages) {
    const expectedSlotId = `${NS_HOST_TYPE_PREFIX}:${page.pageType}`;
    for (const element of elements) {
      populateNamespaceForHostTypeMutably(expectedSlotId, page.id, element);
    }
  }
}

function determinePageTypesForNavigationLayout(
  navigationLayout: NavigationLayout | undefined | null,
): NavigationPageType[] {
  switch (navigationLayout) {
    case 'Top':
      return ['Header'];
    case 'Side':
      return ['Sidebar'];
    case 'Mixed':
      return ['Header', 'Sidebar'];
    case 'TopAndFooter':
      return ['Header', 'Bottombar'];
    default:
      return [];
  }
}
